import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://loyaltyapi.sailabrasive.com';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

const toQueryString = (params: Record<string, any>) =>
  new URLSearchParams(
    Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString();

api.interceptors.request.use((config) => {
  const creds = localStorage.getItem('adminCredentials');
  if (creds) {
    try {
      const { username, password } = JSON.parse(creds);
      if (username && password) {
        config.headers.Authorization = `Basic ${btoa(`${username}:${password}`)}`;
      }
    } catch (error) {
      console.error('Invalid credentials format');
      localStorage.removeItem('adminCredentials');
      localStorage.removeItem('adminUser');
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.warn('Authentication failed, clearing credentials');
      localStorage.removeItem('adminCredentials');
      localStorage.removeItem('adminUser');
      // Only redirect if we're not already on login page
      if (window.location.pathname !== '/console/login' && window.location.pathname.startsWith('/console')) {
        window.location.href = '/console/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// ---- Admin Auth ----
export const adminLogin = (username: string, password: string) =>
  api.post('/api/admin/auth/login', { username, password });

// ---- Dashboard ----
export const getDashboard = () => api.get('/api/admin/dashboard');

// ---- Products ----
export const getProducts = (page = 1, pageSize = 20, search = '') =>
  api.get(`/api/admin/products?page=${page}&pageSize=${pageSize}&search=${search}`);
export const getProduct = (id: number) => api.get(`/api/admin/products/${id}`);
export const createProduct = (data: any) => api.post('/api/admin/products', data);
export const updateProduct = (id: number, data: any) => api.put(`/api/admin/products/${id}`, data);
export const setRewardMapping = (data: any) => api.post('/api/admin/products/reward-mapping', data);

// ---- QR Codes ----
export const generateQrBatch = (data: any) => api.post('/api/admin/qr/generate', data);
export const getQrBatches = (page = 1, pageSize = 20) =>
  api.get(`/api/admin/qr/batches?page=${page}&pageSize=${pageSize}`);
export const getBatchQrCodes = (batchNumber: string) => api.get(`/api/admin/qr/batch/${batchNumber}`);
export const getQrStats = () => api.get('/api/admin/qr/stats');

// ---- Schemes ----
export const getSchemes = (page = 1, pageSize = 20) =>
  api.get(`/api/admin/schemes?page=${page}&pageSize=${pageSize}`);
export const getSchemeOptions = () => api.get('/api/admin/schemes/options');
export const getScheme = (id: number) => api.get(`/api/admin/schemes/${id}`);
export const createScheme = (data: any) => api.post('/api/admin/schemes', data);
export const updateScheme = (id: number, data: any) => api.put(`/api/admin/schemes/${id}`, data);
export const toggleScheme = (id: number) => api.put(`/api/admin/schemes/${id}/toggle`);
export const getCatalogItems = (params: any) => {
  const q = toQueryString(params);
  return api.get(`/api/admin/schemes/catalog?${q}`);
};
export const addCatalogItem = (data: any) => api.post('/api/admin/schemes/catalog', data);
export const updateCatalogItem = (id: number, data: any) => api.put(`/api/admin/schemes/catalog/${id}`, data);
export const deleteCatalogItem = (id: number) => api.delete(`/api/admin/schemes/catalog/${id}`);
export const uploadCatalogImage = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/api/admin/schemes/catalog/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// ---- Users ----
export const getUsers = (params: any) => {
  const q = toQueryString(params);
  return api.get(`/api/admin/users?${q}`);
};
export const getUser = (id: number) => api.get(`/api/admin/users/${id}`);
export const getUserScans = (id: number) => api.get(`/api/admin/users/${id}/scans`);
export const getUserLedger = (id: number, params: any = {}) => {
  const q = toQueryString(params);
  return api.get(`/api/admin/users/${id}/wallet-ledger${q ? `?${q}` : ''}`);
};
export const getPendingUsers = (params: any) => {
  const q = toQueryString(params);
  return api.get(`/api/admin/users/pending?${q}`);
};
export const toggleUser = (id: number) => api.put(`/api/admin/users/${id}/toggle`);
export const verifyUser = (id: number) => api.put(`/api/admin/users/${id}/verify`);
export const unverifyUser = (id: number) => api.put(`/api/admin/users/${id}/unverify`);

// ---- Orders ----
export const getOrders = (params: any) => {
  const q = toQueryString(params);
  return api.get(`/api/admin/orders?${q}`);
};
export const getOrder = (id: number) => api.get(`/api/admin/orders/${id}`);
export const updateOrderStatus = (id: number, data: any) => api.put(`/api/admin/orders/${id}/status`, data);

// ---- Labour Payouts ----
export const getLabourPayouts = (params: any) => {
  const q = toQueryString(params);
  return api.get(`/api/admin/labour-payouts?${q}`);
};
export const updateLabourPayoutStatus = (id: number, data: any) =>
  api.put(`/api/admin/labour-payouts/${id}/status`, data);

// ---- Config ----
export const getConfigs = () => api.get('/api/admin/config');
export const updateConfig = (data: any) => api.put('/api/admin/config', data);

// ---- Pre-Registered Users ----
export const getPreRegistered = (params: any) => {
  const q = toQueryString(params);
  return api.get(`/api/admin/pre-registered?${q}`);
};
export const addPreRegistered = (data: any) => api.post('/api/admin/pre-registered', data);
export const bulkAddPreRegistered = (data: any[]) => api.post('/api/admin/pre-registered/bulk', data);
export const deletePreRegistered = (id: number) => api.delete(`/api/admin/pre-registered/${id}`);

// ---- Labourer (public) ----
export const verifyQr = (code: string) => api.get(`/api/labourer/verify-qr/${code}`);
export const labourerScan = (data: any) => api.post('/api/labourer/scan', data);
